/**
 * Phase 2 / §2.6 — Story 6.1 — Word-count helper + Vuelidate factory.
 *
 * `countMeaningfulWords` is the shared FE side of the BE/FE parity
 * cross-check in Story 6.2 (T-2.6.2-U-05 — BE `count_meaningful_words`
 * must agree with this helper on the same fixture strings). Living
 * in utils (not on a component) keeps that parity easy to reason about.
 *
 * Semantics — distilled from the §2.6 test plan:
 *   - HTML tags are stripped before any counting (General Comments is
 *     rich text via VueEditor, so the field round-trips HTML).
 *   - Punctuation collapses to whitespace. Apostrophes are preserved
 *     so contractions and possessives ("café's") count as one word,
 *     not two — but curly quotation marks ("smart quotes") behave as
 *     punctuation, since they're typographic separators rather than
 *     intra-word characters.
 *   - Whitespace is collapsed; empty tokens are dropped. A token only
 *     counts if it contains at least one letter, digit, or combining
 *     mark — that excludes pure-apostrophe tokens like "'" that
 *     survive the strip but aren't meaningful words.
 *   - Unicode letters and combining marks are first-class. Both NFC
 *     ("café" = c+a+f+é) and NFD ("café" = c+a+f+e+́) inputs
 *     count the same — `\p{M}` keeps combining accents attached to
 *     their base letter.
 */

import { helpers } from 'vuelidate/lib/validators'

const HTML_TAG_RE = /<[^>]*>/g

// "Word character set" — letters (any script), digits, combining marks,
// plus the ASCII apostrophe to keep contractions intact. Anything else
// is treated as a separator and replaced with whitespace.
const NON_WORD_CHARS_RE = /[^\p{L}\p{N}\p{M}'\s]/gu

// Token-validity guard — after splitting on whitespace, we drop any
// token that doesn't contain at least one real letter/digit/mark.
// Catches "'" surviving as a token when an apostrophe is sandwiched
// between separators.
const HAS_WORD_CHAR_RE = /[\p{L}\p{N}\p{M}]/u

export function countMeaningfulWords(input) {
    if (input == null) return 0
    const cleaned = String(input)
        .replace(HTML_TAG_RE, ' ')
        .replace(NON_WORD_CHARS_RE, ' ')
        .trim()
    if (!cleaned) return 0
    const tokens = cleaned.split(/\s+/).filter(t => HAS_WORD_CHAR_RE.test(t))
    return tokens.length
}

/**
 * Vuelidate validator factory — returns `true` when the value contains
 * at least `n` meaningful words. `helpers.withParams` exposes `n` to
 * Vuelidate's error rendering so the rule key shows up as `minWords`
 * with the threshold attached.
 *
 * Usage:
 *   validations: { text: { minWords: minWords(25) } }
 *   // then in template: v-if="$v.text.$dirty && !$v.text.minWords"
 */
export function minWords(n) {
    return helpers.withParams(
        { type: 'minWords', n },
        value => countMeaningfulWords(value) >= n,
    )
}
